import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { 
  Plus, 
  Search,
  TrendingUp, 
  DollarSign, 
  Target,
  Calendar,
  Upload,
  Eye,
  Download,
  CheckCircle,
  MoreHorizontal,
  Edit,
  Share2,
  Trash2,
  Loader2
} from 'lucide-react';
import { useVentureDeals, VentureDeal } from '@/hooks/useVentureDeals';
import { VentureDealCreationModal } from './VentureDealCreationModal';
import { VentureDealDetailModal } from './VentureDealDetailModal';
import { VentureDealEditModal } from './VentureDealEditModal';
import { PDFDealUploadModal } from '@/components/deals/PDFDealUploadModal';
import { DealReportGenerator } from '@/components/brand/DealReportGenerator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

const statusColors = {
  active: 'bg-success/10 text-success',
  pending: 'bg-warning/10 text-warning',
  closed: 'bg-primary/10 text-primary',
  cancelled: 'bg-destructive/10 text-destructive',
};

const priorityColors = {
  high: 'bg-destructive/10 text-destructive',
  medium: 'bg-warning/10 text-warning',
  low: 'bg-muted text-muted-foreground',
};

const dealTypeColors = {
  'equity investment': 'bg-purple-100 text-purple-700',
  'convertible note': 'bg-blue-100 text-blue-700',
  'safe': 'bg-green-100 text-green-700',
  'follow-on': 'bg-orange-100 text-orange-700',
};

export const VentureDealsOverview = () => {
  const { deals, loading, error, createDeal, deleteDeal, getMetrics } = useVentureDeals();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<VentureDeal | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showReportGenerator, setShowReportGenerator] = useState(false);
  const [dealToDelete, setDealToDelete] = useState<VentureDeal | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const metrics = getMetrics();

  // De-duplicate deals on the client as a safeguard
  const uniqueDeals = React.useMemo(() => {
    const map = new Map<string, any>();
    for (const d of deals) {
      const key = `${(d.title || '').trim().toLowerCase()}::${d.venture_profile_id || ''}::${d.deal_value || 0}::${(d.description || '').trim().toLowerCase()}`;
      if (!map.has(key)) map.set(key, d);
    }
    return Array.from(map.values());
  }, [deals]);

  // Keep selectedDeal in sync with updated deals data
  useEffect(() => {
    if (selectedDeal && showDetailModal && uniqueDeals.length > 0) {
      const updatedDeal = uniqueDeals.find(deal => deal.id === selectedDeal.id);
      if (updatedDeal) {
        setSelectedDeal(updatedDeal);
      }
    }
  }, [uniqueDeals, showDetailModal, selectedDeal?.id]);

  const filteredDeals = React.useMemo(() => {
    return uniqueDeals.filter(deal => {
      const matchesSearch = (deal.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (deal.talent?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (deal.description || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || deal.status === statusFilter;
      const matchesType = typeFilter === 'all' || deal.deal_type === typeFilter;
      return matchesSearch && matchesStatus && matchesType;
    });
  }, [uniqueDeals, searchTerm, statusFilter, typeFilter]);

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-3 text-muted-foreground">Loading investment deals...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-destructive mb-2">Error Loading Data</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button 
              onClick={() => window.location.reload()} 
              variant="secondary"
            >
              Refresh Page
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const calculateProgress = (deal: VentureDeal) => {
    if (!deal.tasks || deal.tasks.length === 0) return 0;
    const completed = deal.tasks.filter(t => t.status === 'completed').length;
    return Math.round((completed / deal.tasks.length) * 100);
  };

  const handleViewDeal = (deal: VentureDeal) => {
    setSelectedDeal(deal);
    setShowDetailModal(true);
  };

  const handleEditDeal = (deal: VentureDeal) => {
    setSelectedDeal(deal);
    setShowEditModal(true);
  };

  const handleShareDeal = (deal: VentureDeal) => {
    const dealUrl = `${window.location.origin}/deals/${deal.id}`;
    navigator.clipboard.writeText(dealUrl).then(() => {
      toast({
        title: "Link Copied",
        description: "Deal link has been copied to clipboard.",
      });
    }).catch(() => {
      toast({
        title: "Error",
        description: "Failed to copy link to clipboard.",
        variant: "destructive",
      });
    });
  };

  const handleDeleteDeal = async () => {
    if (!dealToDelete) return;

    setIsDeleting(true);
    try {
      await deleteDeal(dealToDelete.id);
      
      toast({
        title: "Deal Deleted",
        description: `${dealToDelete.title} has been deleted.`,
      });
      
      setDealToDelete(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete deal. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const DealCard = ({ deal }: { deal: VentureDeal }) => {
    const progress = calculateProgress(deal);
    
    return (
      <Card className="hover:shadow-md transition-shadow">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="text-lg">{deal.title || 'Untitled Deal'}</CardTitle>
              <CardDescription className="mt-1">
                {deal.talent?.name || 'Portfolio Company'}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={statusColors[deal.status as keyof typeof statusColors]}>
                {deal.status}
              </Badge>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleViewDeal(deal)}>
                    <Eye className="h-4 w-4 mr-2" />
                    View Details
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleEditDeal(deal)}>
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Deal
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleShareDeal(deal)}>
                    <Share2 className="h-4 w-4 mr-2" />
                    Share Deal
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => setDealToDelete(deal)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Deal
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Investment</span>
              <span className="font-semibold">{formatCurrency(deal.deal_value || 0)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Type</span>
              <Badge variant="outline" className={dealTypeColors[deal.deal_type as keyof typeof dealTypeColors]}>
                {deal.deal_type}
              </Badge>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Priority</span>
              <Badge variant="outline" className={priorityColors[deal.priority as keyof typeof priorityColors]}>
                {deal.priority}
              </Badge>
            </div>
            {deal.estimated_completion_date && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Target Close</span>
                <span>{formatDate(deal.estimated_completion_date)}</span>
              </div>
            )}
            <div className="space-y-2 pt-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Progress</span>
                <span className="font-medium">{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1"
                onClick={() => handleViewDeal(deal)}
              >
                <Eye className="h-4 w-4 mr-1" />
                View
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1"
                onClick={() => handleEditDeal(deal)}
              >
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowReportGenerator(true)}>
            <Download className="h-4 w-4 mr-2" />
            Download Report
          </Button>
          <Button variant="outline" onClick={() => setShowUploadModal(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Upload Existing Deal
          </Button>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Deal
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Deals</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalDeals}</div>
            <p className="text-xs text-muted-foreground">
              {metrics.activeDeals} active, {metrics.closedDeals} closed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Investment</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(metrics.totalInvestment)}</div>
            <p className="text-xs text-muted-foreground">
              Avg: {formatCurrency(metrics.avgDealSize)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Deals</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.activeDeals}</div>
            <p className="text-xs text-muted-foreground">
              {metrics.totalDeals} total deals
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Performance</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.closedDeals}</div>
            <p className="text-xs text-muted-foreground">closed deals</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search deals or companies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="equity investment">Equity Investment</SelectItem>
            <SelectItem value="convertible note">Convertible Note</SelectItem>
            <SelectItem value="safe">SAFE</SelectItem>
            <SelectItem value="follow-on">Follow-on</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Deals Grid with Tabs */}
      <Tabs defaultValue="deals" className="space-y-4">
        <TabsList>
          <TabsTrigger value="deals">Deals</TabsTrigger>
          <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="deals" className="space-y-4">
          {filteredDeals.length === 0 ? (
            <Card className="p-8 text-center">
              <div className="text-muted-foreground">
                {deals.length === 0 ? 'No investment deals yet. Create your first deal to get started.' : 'No deals match your filters.'}
              </div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDeals.map((deal) => (
                <DealCard key={deal.id} deal={deal} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="campaigns" className="space-y-4">
          <Card className="p-8 text-center">
            <div className="text-muted-foreground">
              Campaigns are not typically used for investment deals.
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <Card className="p-8 text-center">
            <div className="text-muted-foreground">
              Investment analytics coming soon.
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <VentureDealCreationModal 
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
      />

      <VentureDealDetailModal 
        deal={selectedDeal}
        open={showDetailModal}
        onOpenChange={setShowDetailModal}
      />

      <VentureDealEditModal 
        deal={selectedDeal}
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
      />
      
      <PDFDealUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onDealCreated={() => {
          setShowUploadModal(false);
          window.location.reload();
        }}
        createDeal={createDeal}
      />

      <DealReportGenerator
        open={showReportGenerator}
        onOpenChange={setShowReportGenerator}
        deals={deals}
        campaigns={[]}
        metrics={metrics}
      />

      <AlertDialog open={!!dealToDelete} onOpenChange={(open) => !open && setDealToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Investment Deal?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{dealToDelete?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDeal}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
